import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';

type Stage = 'request' | 'verify';
const CODE_LENGTH = 8;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<Stage>('request');
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resetFlow = () => {
    setStage('request');
    setCode('');
    setMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setMessage('Enter an email address to continue.');
      return;
    }

    if (stage === 'request') {
      setLoading(true);
      setMessage(null);
      const { error } = await supabase.auth.signInWithOtp({ email: trimmedEmail });
      setLoading(false);

      if (error) {
        setMessage(error.message);
        return;
      }

      setMessage(`We emailed you an ${CODE_LENGTH}-digit code. Enter it below to finish signing in.`);
      setStage('verify');
      return;
    }

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setMessage(`Enter the ${CODE_LENGTH}-digit code we emailed you.`);
      return;
    }

    if (trimmedCode.length !== CODE_LENGTH) {
      setMessage(`Codes must be exactly ${CODE_LENGTH} digits.`);
      return;
    }

    setLoading(true);
    setMessage(null);
    const { error } = await supabase.auth.verifyOtp({
      type: 'email',
      email: trimmedEmail,
      token: trimmedCode,
    });
    setLoading(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage('Success! You are now signed in.');
  };

  const stageCopy = stage === 'request' ? 'Request access' : 'Verify code';

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="login-card"
    >
      {stageCopy ? <span className="login-stage">{stageCopy}</span> : null}
      <label htmlFor="email" className="login-label">
        Email
        <input
          id="email"
          type="email"
          className="login-input"
          placeholder="user@shanework.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={loading || stage === 'verify'}
          autoComplete="email"
          required
        />
      </label>
      {stage === 'verify' ? (
        <div className="code-header">
          <button type="button" className="login-secondary stage-switch" onClick={resetFlow} disabled={loading}>
            Switch email
          </button>
        </div>
      ) : null}
      {stage === 'verify' ? (
        <label htmlFor="code" className="login-label">
          {CODE_LENGTH}-digit code
          <input
            id="code"
            type="text"
            className="login-input"
            inputMode="numeric"
            pattern={`\\d{${CODE_LENGTH}}`}
            maxLength={CODE_LENGTH}
            placeholder="00000000"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={loading}
            autoComplete="one-time-code"
            required
          />
          <span className="muted login-hint">Check your inbox for the latest code.</span>
        </label>
      ) : null}
      <div className="login-actions">
        <button type="submit" className="login-primary" disabled={loading}>
          {loading ? 'Processing…' : 'Sign in'}
        </button>
      </div>
      {message ? <p className="muted login-message">{message}</p> : null}
      <div className="login-meta">
        <p className="muted">
          Need an account?{' '}
          <a className="login-link" href="mailto:access@shanework.com?subject=Shanework%20Access%20Request">
            Request access
          </a>
        </p>
      </div>
    </form>
  );
};

export default Login;

